/**
 * DocumentReviewRenderer — Sprint 12 hand-designed renderer for
 * displayType='document_review'.
 *
 * Per spec §8.5: "side-by-side: original / proposed change; redline view;
 * ITAR classification banner".
 *
 * Layout
 * ------
 *   - Top: classification banner (CUI / ITAR) — red if evidence.classification
 *     declares sensitivity='cui' or exportControl='itar'.
 *   - Two-column side-by-side body:
 *       LEFT  — "Original"  read from canonical paths content.original /
 *                            original / before.body / etc.
 *       RIGHT — "Proposed"  read from canonical paths content.proposed /
 *                            proposed / after.body / etc.
 *     Word-level LCS diff colours added words green and removed words red so
 *     reviewers can scan the change at a glance without a 3rd-party diff lib
 *     (no `diff` or `react-diff-viewer` is in the workspace's package.json).
 *   - Below the diff: any declared editable fields from the schema, rendered
 *     via the shared FieldRow primitive. The canonical "justification" field
 *     (multiline + aiEditable per spec) gets a data-justification-field hook
 *     so Playwright can target it.
 *   - Bottom: ActionBar with the schema's actions.
 *
 * Evidence shape (we tolerate flat AND content.*-namespaced layouts):
 *   evidence.original                  string                — preferred
 *   evidence.content.original          string
 *   evidence.before.body / .text       string                — fallback
 *   evidence.proposed                  string                — preferred
 *   evidence.content.proposed          string
 *   evidence.after.body / .text        string                — fallback
 *   evidence.classification            { sensitivity, exportControl }
 *   evidence.content.classification    same shape
 *   evidence.document_title            string                — header chip
 *
 * Why not a 3rd-party diff library
 * --------------------------------
 * `package.json` does not include `diff` or `react-diff-viewer`. Adding a
 * new dependency is out-of-scope for a renderer polish PR — and a robust
 * word-level LCS diff is ~40 lines of code. Reviewers get the same
 * additive / subtractive colour signal they'd get from a library.
 *
 * Reuse
 * -----
 * - FieldRow + ActionBar are imported from the shared primitives in
 *   AutoRenderedApprovalCard.tsx so AI-edit + edit-chain integrity is
 *   identical to every other approval surface.
 * - readPath comes from @argusai/shared (single source of truth for
 *   dotted-path resolution).
 *
 * Tests in __tests__/auto-rendered-approval-card.test.ts (Sprint 12 block).
 */

import * as React from 'react';
import { FileText, Lock, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { readPath } from '@argusai/shared';
import type { FieldDef } from '@argusai/shared';
import { FieldRow } from '../AutoRenderedApprovalCard';
import { ActionBar } from '../ActionBar';
import { cn } from '@/lib/utils';
import type { RendererComponent } from '../AutoRenderedApprovalCard';
import type { ExportControl, Sensitivity } from '@argusai/shared';

// Canonical paths the renderer probes (first hit wins).
const ORIGINAL_PATHS = [
  'content.original',
  'original',
  'before.body',
  'before.text',
  'before',
] as const;
const PROPOSED_PATHS = [
  'content.proposed',
  'proposed',
  'after.body',
  'after.text',
  'after',
] as const;
const CLASSIFICATION_PATHS = [
  'content.classification',
  'classification',
] as const;
const TITLE_PATHS = [
  'content.document_title',
  'document_title',
  'content.title',
  'title',
] as const;

function asString(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

function firstString(
  evidence: Record<string, unknown>,
  paths: readonly string[],
): string {
  for (const p of paths) {
    const v = readPath(evidence, p);
    const s = asString(v);
    if (s !== '') return s;
  }
  return '';
}

interface Classification {
  sensitivity: Sensitivity | string;
  exportControl: ExportControl | string;
}

function readClassification(
  evidence: Record<string, unknown>,
): Classification | null {
  for (const p of CLASSIFICATION_PATHS) {
    const raw = readPath(evidence, p);
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const r = raw as Record<string, unknown>;
      const sensitivity = asString(r.sensitivity).toLowerCase();
      const exportControl = asString(r.exportControl).toLowerCase();
      if (sensitivity || exportControl) {
        return {
          sensitivity: sensitivity || 'unclassified',
          exportControl: exportControl || 'none',
        };
      }
    }
  }
  return null;
}

// ─── Word-level LCS diff ────────────────────────────────────────────────────
//
// Splits both inputs on whitespace + punctuation boundaries (keeping
// separators as their own tokens so spacing is preserved in the rendered
// output), computes the longest common subsequence via classic DP, and
// emits an ordered list of segments tagged `added | removed | unchanged`.
// O(n*m) memory — fine for documents in the hundreds-of-KB range that an
// approval card would realistically surface. Larger inputs are clamped
// upstream by the schema's evidence size limit.

type Segment = { status: 'added' | 'removed' | 'unchanged'; text: string };

const TOKEN_RE = /(\s+|[\p{P}])/u;

function tokenize(s: string): string[] {
  // Split keeping separators so we can join cleanly without inventing
  // whitespace. Empty tokens are dropped.
  return s.split(TOKEN_RE).filter((t) => t !== '' && t !== undefined);
}

/** Diff two strings at word granularity. */
export function diffWords(
  original: string,
  proposed: string,
  maxTokens = 4000,
): Segment[] {
  const aTokens = tokenize(original).slice(0, maxTokens);
  const bTokens = tokenize(proposed).slice(0, maxTokens);
  const n = aTokens.length;
  const m = bTokens.length;
  if (n === 0 && m === 0) return [];
  if (n === 0) {
    return [{ status: 'added', text: bTokens.join('') }];
  }
  if (m === 0) {
    return [{ status: 'removed', text: aTokens.join('') }];
  }
  // LCS table.
  // Allocate compactly: one row of (m+1) numbers, rolled forward, plus a
  // bitmap of choices so we can backtrack. For documents larger than a
  // few thousand tokens we fall back to a coarse line-level diff (below)
  // because the full DP would be O(16M) which is fine in JS but the
  // backtrack is 16M entries.
  if (n * m > 1_000_000) {
    return diffLines(original, proposed);
  }
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0),
  );
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (aTokens[i - 1] === bTokens[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  // Backtrack to collect per-token moves, then re-walk in document order
  // before merging. Backtracking yields moves in REVERSE doc order, and a
  // naive `pushSeg` + `reverse()` would corrupt the text within a merged
  // segment (because we'd concatenate "here"+"content" but the desired
  // order is "content"+"here"). Collecting moves first lets us keep the
  // text in forward order.
  const moves: Segment[] = [];
  let i = n;
  let j = m;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && aTokens[i - 1] === bTokens[j - 1]) {
      moves.push({ status: 'unchanged', text: aTokens[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      moves.push({ status: 'added', text: bTokens[j - 1] });
      j--;
    } else {
      moves.push({ status: 'removed', text: aTokens[i - 1] });
      i--;
    }
  }
  moves.reverse();
  const segments: Segment[] = [];
  for (const m of moves) pushSeg(segments, m);
  return mergeSegments(segments);
}

/**
 * Line-level fallback for very large documents. Much smaller table at
 * the cost of granularity. Authors hitting this path see paragraph-level
 * highlighting rather than word-level.
 */
function diffLines(original: string, proposed: string): Segment[] {
  const a = original.split(/(\r?\n)/);
  const b = proposed.split(/(\r?\n)/);
  if (a.length === 0 && b.length === 0) return [];
  const out: Segment[] = [];
  // Trivial line-by-line — paragraphs that match index-for-index stay
  // unchanged, the rest is "remove then add" in document order.
  const max = Math.max(a.length, b.length);
  for (let i = 0; i < max; i++) {
    const al = a[i];
    const bl = b[i];
    if (al === bl && al !== undefined) {
      pushSeg(out, { status: 'unchanged', text: al });
      continue;
    }
    if (al !== undefined) pushSeg(out, { status: 'removed', text: al });
    if (bl !== undefined) pushSeg(out, { status: 'added', text: bl });
  }
  return mergeSegments(out);
}

function pushSeg(arr: Segment[], next: Segment): void {
  const last = arr[arr.length - 1];
  if (last && last.status === next.status) {
    last.text = last.text + next.text;
    return;
  }
  arr.push({ ...next });
}

function mergeSegments(arr: Segment[]): Segment[] {
  // Already merged by pushSeg, but normalise: drop empty.
  return arr.filter((s) => s.text !== '');
}

// ─── Classification banner ──────────────────────────────────────────────────

function ClassificationBanner({ classification }: { classification: Classification | null }) {
  if (!classification) return null;
  const isItar = classification.exportControl === 'itar';
  const isCui = classification.sensitivity === 'cui';
  const isRestricted = classification.sensitivity === 'restricted';
  if (!isItar && !isCui && !isRestricted) return null;
  const label = isItar
    ? 'ITAR — Export-Controlled'
    : isRestricted
      ? 'RESTRICTED'
      : 'CUI';
  return (
    <div
      data-classification-banner="true"
      data-itar={isItar ? 'true' : 'false'}
      data-cui={isCui ? 'true' : 'false'}
      className={cn(
        'flex items-center gap-2 rounded-md border-2 px-3 py-2 text-sm font-semibold',
        isItar
          ? 'border-red-500 bg-red-50 text-red-900 dark:bg-red-950/40 dark:text-red-300'
          : 'border-amber-500 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-300',
      )}
    >
      <ShieldAlert className="h-4 w-4" />
      <span className="uppercase tracking-wider">{label}</span>
      <span className="ml-2 text-xs font-normal opacity-90">
        Handle per applicable export-control regulations. Do not distribute
        outside cleared personnel.
      </span>
    </div>
  );
}

// ─── Document pane ─────────────────────────────────────────────────────────

function DocumentPane({
  title,
  body,
  segments,
  side,
}: {
  title: string;
  body: string;
  segments: Segment[];
  side: 'original' | 'proposed';
}) {
  return (
    <div
      data-document-pane={side}
      className="flex min-h-[16rem] flex-col rounded-md border border-border/60 bg-card"
    >
      <header className="flex items-center gap-1.5 border-b border-border/40 bg-muted/30 px-3 py-1.5">
        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </span>
      </header>
      <div
        className="flex-1 overflow-auto whitespace-pre-wrap break-words p-3 text-sm leading-relaxed"
        data-document-body={side}
      >
        {segments.length === 0 ? (
          body === '' ? (
            <span className="italic text-muted-foreground">
              No content
            </span>
          ) : (
            <span>{body}</span>
          )
        ) : (
          segments
            .filter((seg) => {
              if (side === 'original') return seg.status !== 'added';
              return seg.status !== 'removed';
            })
            .map((seg, i) => (
              <span
                key={i}
                data-diff-status={seg.status}
                className={cn(
                  seg.status === 'added' &&
                    'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300',
                  seg.status === 'removed' &&
                    'bg-rose-100 text-rose-900 line-through dark:bg-rose-950/40 dark:text-rose-300',
                )}
              >
                {seg.text}
              </span>
            ))
        )}
      </div>
    </div>
  );
}

// ─── Renderer ───────────────────────────────────────────────────────────────

export const DocumentReviewRenderer: RendererComponent = ({
  approval,
  schema,
  evidence,
  onFieldChange,
  onDecide,
  disabled,
}) => {
  const original = firstString(evidence, ORIGINAL_PATHS);
  const proposed = firstString(evidence, PROPOSED_PATHS);
  const classification = readClassification(evidence);
  const docTitle = firstString(evidence, TITLE_PATHS);

  // Memoise the diff — useful for long documents.
  const segments = React.useMemo(
    () => diffWords(original, proposed),
    [original, proposed],
  );

  // The "justification" / explanation field is typically declared by the
  // workflow author. We don't fork it — we just render via FieldRow and tag
  // the wrapper for Playwright. Any other editable fields render below it.
  const justificationField = (schema.editableFields as FieldDef[]).find(
    (f: FieldDef) =>
      f.path === 'justification' ||
      f.path === 'content.justification' ||
      f.path === 'explanation' ||
      f.path === 'reason',
  );
  const otherFields = (schema.editableFields as FieldDef[]).filter(
    (f: FieldDef) => f !== justificationField,
  );

  // Stats for the change-summary chip.
  const stats = React.useMemo(() => {
    let added = 0;
    let removed = 0;
    for (const seg of segments) {
      const wordCount = seg.text.trim().length > 0
        ? seg.text.trim().split(/\s+/).length
        : 0;
      if (seg.status === 'added') added += wordCount;
      else if (seg.status === 'removed') removed += wordCount;
    }
    return { added, removed };
  }, [segments]);

  return (
    <div className="space-y-4 p-4" data-renderer="document_review">
      <ClassificationBanner classification={classification} />

      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-primary" />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Document review
          </span>
          {docTitle && (
            <Badge
              variant="outline"
              data-document-title="true"
              className="ml-1 max-w-xs truncate"
              title={docTitle}
            >
              {docTitle}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
          <span
            data-change-summary="added"
            className="rounded bg-emerald-100 px-2 py-0.5 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300"
          >
            +{stats.added}
          </span>
          <span
            data-change-summary="removed"
            className="rounded bg-rose-100 px-2 py-0.5 text-rose-900 dark:bg-rose-950/40 dark:text-rose-300"
          >
            -{stats.removed}
          </span>
        </div>
      </header>

      {/* Side-by-side diff. Stacks on small screens to stay legible. */}
      <div
        data-document-diff="true"
        className="grid gap-3 lg:grid-cols-2"
      >
        <DocumentPane
          title="Original"
          body={original}
          segments={segments}
          side="original"
        />
        <DocumentPane
          title="Proposed change"
          body={proposed}
          segments={segments}
          side="proposed"
        />
      </div>

      {/* Editable justification + extras. */}
      {(justificationField || otherFields.length > 0) && (
        <section className="space-y-3 border-t border-border/60 pt-3">
          {justificationField && (
            <div data-justification-field="true">
              <FieldRow
                approvalId={approval.id}
                field={justificationField}
                value={readPath(evidence, justificationField.path)}
                onChange={(v) => onFieldChange(justificationField.path, v)}
                disabled={disabled}
              />
            </div>
          )}
          {otherFields.map((f: FieldDef) => (
            <FieldRow
              key={f.path}
              approvalId={approval.id}
              field={f}
              value={readPath(evidence, f.path)}
              onChange={(v) => onFieldChange(f.path, v)}
              disabled={disabled}
            />
          ))}
        </section>
      )}

      <ActionBar
        actions={schema.actions}
        approvalId={approval.id}
        onDecide={onDecide}
        disabled={disabled}
        className="rounded-md"
      />
    </div>
  );
};
